
import { cn } from "@/lib/utils";
import React from 'react';

export type SellerTier = "Bronze" | "Silver" | "Gold" | "Platinum" | "Founder";

type SellerTierProps = {
  tier: SellerTier | null;
  className?: string;
  showName?: boolean;
};

const tierConfig = {
  Bronze: {
    className: "tier-bronze",
    name: "Bronze"
  },
  Silver: {
    className: "tier-silver",
    name: "Silver"
  },
  Gold: {
    className: "tier-gold",
    name: "Gold"
  },
  Platinum: {
    className: "tier-platinum",
    name: "Platinum"
  },
  Founder: {
    className: "tier-founder",
    name: "Founder"
  },
};

/**
 * A small seeded random source (FNV-1a over the seed, then mulberry32), so a
 * badge's sparkles are scattered the same way wherever the same badge renders.
 */
function seededRandom(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let state = h >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const SellerTierComponent = ({ tier, className, showName = true }: SellerTierProps) => {
  // The sparkles' scatter, drawn from a source seeded by this badge's React id.
  // It used to be Math.random() on every render, which the server and the
  // browser's hydrating render could never agree on now that product pages and
  // storefronts render on the server, and which re-scattered the sparkles on
  // every re-render. useId is the same on both sides and differs per badge, so
  // badges side by side still scatter differently.
  const id = React.useId();
  const sparkles = React.useMemo(() => {
    const random = seededRandom(`${id}:${tier}`);
    if (tier === 'Gold') {
      return Array.from({ length: 4 }, () => ({
        '--tx': Math.floor(random() * 20 - 10),
        '--ty': Math.floor(random() * 20 - 10),
      } as React.CSSProperties));
    }
    if (tier === 'Platinum') {
      return Array.from({ length: 20 }, () => ({
        top: `${random() * 100}%`,
        left: `${random() * 100}%`,
        '--duration': `${(random() * 2 + 2).toFixed(2)}s`,
        '--delay': `${(random() * -2).toFixed(2)}s`,
      } as React.CSSProperties));
    }
    return [];
  }, [id, tier]);

  if (!tier) {
    return null;
  }

  const config = tierConfig[tier];

  // If tier is an invalid value, config will be undefined.
  if (!config) {
    return null;
  }

  return (
    <div className={cn("tier-badge", config.className, className)}>
      {/* The sparkles' own layer clips them to the pill (globals.css), so the
          badge need not: Platinum's glow draws outside it. Hidden under
          reduced motion. */}
      {(tier === 'Gold' || tier === 'Platinum') && (
        <span className="tier-sparkles" aria-hidden="true">
          {tier === 'Gold' ? (
            <>
              <span className="star star-1" style={sparkles[0]}></span>
              <span className="star star-2" style={sparkles[1]}></span>
              <span className="star star-3" style={sparkles[2]}></span>
              <span className="star star-4" style={sparkles[3]}></span>
            </>
          ) : (
            sparkles.map((style, i) => (
              <span key={i} className="star" style={style}></span>
            ))
          )}
        </span>
      )}
      {showName && <span className="tier-name">{config.name}</span>}
    </div>
  );
}

export const SellerTier = React.memo(SellerTierComponent);
